import React, { useState } from "react";
import BoostAcademics from "./BoostAcademics";
import BoostBehaviour from "./BoostBehaviour";
import Celebrate from "./Celebrate";
import { BookOpen, Heart, ThumbsUp, Lightbulb, Info } from "lucide-react";

// Calculate Number of Wellness Points
const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Utility functions
export const randomInteger = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const defineRecommendationType = () => {
  const randomNumber = randomInteger(0, 100);
  if (randomNumber <= 25) {
    return "Frequency";
  } else {
    return "Change";
  }
};

export const getApplicableEmotions = (type) => {
  if (type === "Frequency") {
    return "Sad";
  } else {
    return "Happy";
  }
};

export const definePluralOrSingular = (isPlural = true) => {
  // TODO: Add logic to define plural or singular depending on the number of students
  if (isPlural) {
    return "plural";
  } else {
    return "single";
  }
};

export const getHighestCurrentPercentEmotion = (emotionPercents) => {
  emotionPercents.sort((a, b) => b.currentPercent - a.currentPercent);
  return emotionPercents[0];
};

export const getHighestChange = (emotionPercents) => {
  emotionPercents.sort((a, b) => b.change - a.change);
  return emotionPercents[0];
};

export const checkRecData = (emotionPercents) => {
  let sum = 0;
  emotionPercents.forEach((emotion) => {
    sum += emotion.currentPercent;
  });
  if (sum === 0) {
    return false;
  } else {
    return true;
  }
};

// Normalize path variables to handle directory name mismatches
const normalizePath = (pathVar) => {
  if (typeof pathVar !== "string") return pathVar;

  // Handle known directory name mismatches
  const directoryMappings = {
    // Removed mapping for "Give someone a high five" since directory name has been fixed
  };

  return directoryMappings[pathVar] || pathVar;
};

export const fetchDataFromFiles = async (path_variables) => {
  let path = "/assets/recommendations/";

  if (path_variables.length === 0) {
    return "No path variables provided";
  }

  try {
    // Normalize path variables before building path
    const normalizedVariables = path_variables.map(normalizePath);

    normalizedVariables.forEach((variable) => {
      path += `${variable}/`;
    });
    path = path.slice(0, -1);
    path += ".json";

    let response = await fetch(path);

    // If first attempt fails and we have an action in path, try without leading/trailing spaces
    if (!response.ok && path_variables.length >= 2) {
      const actionIndex = 1; // Assuming action is the second path variable
      const originalAction = path_variables[actionIndex];

      if (typeof originalAction === "string") {
        // Try with trimmed action name
        const trimmedVariables = [...normalizedVariables];
        trimmedVariables[actionIndex] = originalAction.trim();

        let fallbackPath = "/assets/recommendations/";
        trimmedVariables.forEach((variable) => {
          fallbackPath += `${variable}/`;
        });
        fallbackPath = fallbackPath.slice(0, -1) + ".json";
        response = await fetch(fallbackPath);

        if (response.ok) {
          path = fallbackPath; // Update path for logging
        }
      }
    }

    if (!response.ok) {
      return {
        status: "error",
        message: `File not found: ${path} (Status: ${response.status})`,
      };
    }

    // Check if response is actually JSON before parsing
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      return {
        status: "error",
        message: `Expected JSON but received: ${contentType}`,
      };
    }

    const data = await response.json();
    return { status: "success", data: data };
  } catch (error) {
    return {
      status: "error",
      message: "An error occurred while fetching data from files.",
    };
  }
};

export const fetchCelebrations = async (
  currentConsideration,
  type,
  plural_or_singular,
  currentTab
) => {
  try {
    const response = await fetchDataFromFiles([
      currentTab,
      currentConsideration,
      type,
      plural_or_singular,
    ]);

    if (response.status === "error") {
      return response.data;
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchInsights = async (
  emotion,
  type,
  plural_or_singular,
  currentTab
) => {
  try {
    const response = await fetchDataFromFiles([
      currentTab,
      emotion,
      type,
      plural_or_singular,
    ]);

    if (response.status === "error") {
      // Return a fallback insight
      return {
        insights: "No specific insights available for this combination.",
      };
    }

    return response.data;
  } catch (error) {
    // Return fallback instead of throwing
    return {
      insights: "Unable to load insights at this time.",
    };
  }
};

export const fetchConsiderations = async (
  argument_variable,
  plural_or_singular,
  setConsiderations,
  currentTab
) => {
  try {
    const data_details = await fetchDataFromFiles([
      currentTab,
      argument_variable,
      "Considerations",
      plural_or_singular,
    ]);

    if (data_details.status === "error") {
      setConsiderations("No considerations available for this selection.");
      return;
    }

    const data = data_details.data;
    if (!data.considerations || data.considerations.length === 0) {
      setConsiderations("No considerations available for this selection.");
      return;
    }

    const randomIndex = randomInteger(0, data.considerations.length - 1);
    const consideration = data.considerations[randomIndex];
    setConsiderations(
      consideration || "No considerations available for this selection."
    );
  } catch (error) {
    setConsiderations("Unable to load considerations at this time.");
  }
};

const Recommendations = ({ state, emotionPercents, name }) => {
  const [insights, setInsights] = useState("");
  const [recommendationType, setRecommendationType] = useState("");
  const [applicableEmotion, setApplicableEmotion] = useState("");
  const [pluralOrSingular, setPluralOrSingular] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [considerations, setConsiderations] = useState("");
  const [selectedRecommendationTab, setSelectedRecommendationTab] =
    useState("BoostAcademics");
  const getRecommendations = async (selectedTab) => {
    try {
      // Set the selected tab
      setSelectedRecommendationTab(selectedTab);
    } catch (error) {
      // setInsights("An error occurred while getting recommendations.");
    } finally {
      //   setIsLoading(false);
    }
  };

  return (
    <div
      className="flex-1 bg-white shadow rounded p-3 sm:p-4 2xl:p-8"
      data-onboarding-id="educator-recommendations"
    >
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-5 h-5 text-600 shrink-0" />
        <h3 className="text-[22px] sm:text-2xl font-bold">
          {name && state.chosenStudent && state.chosenStudent !== "All Students"
          ? `${name}'s Recommendations`
          : "Data-based Recommendations"}
        </h3>
        <div className="group relative inline-block">
          <Info className="w-4 h-4 text-600 text-[#5b9cd6] hover:text-gray-600 cursor-pointer transition-colors" />
          <div className="absolute hidden group-hover:block right-1/2 sm:right-0 2xl:left-0 mt-2 w-48 lg:w-64 mt-2 w-64 p-3 text-sm bg-gray-100 rounded shadow z-50">
            <span className="text-400 text-[14px]">
              Click on Boost Academics, Boost Behavior or Celebrate to view
              data-based recommendations.
            </span>
          </div>
        </div>
      </div>
      <h4 className="text-400 text-[18px] ml-7">
        Discover academic and behavior boosters.
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] sm:gap-5">
        <div className="flex flex-row sm:flex-col gap-1 sm:gap-2">
          <button
            className={`text-left text-[16px] p-2 sm:p-3 rounded flex items-center gap-1 sm:gap-2 mt-3
                ${
                  selectedRecommendationTab === "BoostAcademics"
                    ? "bg-[#F3F8FD] border-b border-b-1 border-[#005BE2] text-700 font-bold"
                    : "opacity-80 hover:opacity-100 text-400"
                }
                transition-colors`}
            disabled={isLoading}
            onClick={() => getRecommendations("BoostAcademics")}
          >
            <div className="sm:w-6 sm:h-6 flex items-center justify-center shrink-0">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <span className="hidden sm:block">{isLoading ? "Loading..." : "Boost Academics"}</span>
            <span className="sm:hidden text-sm">{isLoading ? "Loading..." : "Academics"}</span>
          </button>
          <button
            className={`text-left text-[16px] p-2 sm:p-3 rounded flex items-center gap-1 sm:gap-2 mt-3
                ${
                  selectedRecommendationTab === "BoostBehaviour"
                    ? "bg-[#F3F8FD]  border-b border-b-1 border-[#005BE2] text-700 font-bold"
                    : "opacity-80 hover:opacity-100 text-400"
                }
                transition-colors`}
            disabled={isLoading}
            onClick={() => getRecommendations("BoostBehaviour")}
          >
            <div className="sm:w-6 sm:h-6 flex items-center justify-center shrink-0">
              <ThumbsUp className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <span className="hidden sm:block">{isLoading ? "Loading..." : "Boost Behavior"}</span>
            <span className="sm:hidden text-sm">{isLoading ? "Loading..." : "Behavior"}</span>
          </button>
          <button
            className={`text-left text-[16px] p-2 sm:p-3 rounded flex items-center gap-1 sm:gap-2 mt-3
              ${
                selectedRecommendationTab === "Celebrate"
                  ? "bg-[#F3F8FD] border-b border-b-1 border-[#005BE2] text-700 font-bold"
                  : "opacity-80 hover:opacity-100 text-400"
              }
              transition-colors`}
            disabled={isLoading}
            onClick={() => getRecommendations("Celebrate")}
          >
            <div className="sm:w-6 sm:h-6 flex items-center justify-center shrink-0">
              <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <span className="text-sm sm:text-base">{isLoading ? "Loading..." : "Celebrate"}</span>
          </button>
        </div>

        <div className="flex flex-col gap-3 border p-3 rounded bg-white shadow-sm mt-3">
          {selectedRecommendationTab === "BoostAcademics" && (
            <BoostAcademics
              selectedRecommendationTab={selectedRecommendationTab}
              emotionPercents={emotionPercents}
              {...Object.fromEntries(
                Object.entries(state).filter(([key]) =>
                  ["chosenTime"].includes(key)
                )
              )}
              state={state}
            />
          )}

          {selectedRecommendationTab === "BoostBehaviour" && (
            <BoostBehaviour
              selectedRecommendationTab={selectedRecommendationTab}
              actionCounts={state.actionCounts}
              {...Object.fromEntries(
                Object.entries(state).filter(([key]) =>
                  ["chosenTime"].includes(key)
                )
              )}
              state={state}
            />
          )}
          {selectedRecommendationTab === "Celebrate" && (
            <Celebrate
              selectedRecommendationTab={selectedRecommendationTab}
              emotionPercents={emotionPercents}
              actionsCounter={formatNumber(state.actionsCounter)}
              studentDataLength={formatNumber(state.allWellnessPoints.length)}
              actionCounts={state.actionCounts}
              filteredDataPoints={state.filteredDataPoints}
              allStudents={state.allStudents}
              studentEmailToNameMap={state.studentEmailToNameMap}
              state={state}
              {...Object.fromEntries(
                Object.entries(state).filter(([key]) =>
                  ["chosenTime"].includes(key)
                )
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Recommendations;
